import { type ReactNode, useCallback, useEffect, useState } from "react";

import { IconButton } from "../Button/IconButton";

interface CenteredModalProps {
	isOpen: boolean;
	onClose: () => void;
	children: ReactNode;
}

export function CenteredModal({
	isOpen,
	onClose,
	children,
}: CenteredModalProps) {
	const [mounted, setMounted] = useState(false);
	const [visible, setVisible] = useState(false);

	// Enter: mount first, then flip visible on next frame to trigger transition
	useEffect(() => {
		if (isOpen) {
			setMounted(true);
			requestAnimationFrame(() => {
				requestAnimationFrame(() => setVisible(true));
			});
		} else {
			setVisible(false);
		}
	}, [isOpen]);

	// Leave: unmount after transition ends
	const handleTransitionEnd = useCallback(() => {
		if (!visible) {
			setMounted(false);
		}
	}, [visible]);

	// Close modal on Escape key
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape" && isOpen) {
				onClose();
			}
		};

		document.addEventListener("keydown", handleEscape);
		return () => document.removeEventListener("keydown", handleEscape);
	}, [isOpen, onClose]);

	// Prevent body scroll when modal is open
	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
		}

		return () => {
			document.body.style.overflow = "unset";
		};
	}, [isOpen]);

	if (!mounted) return null;

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center p-4"
			onTransitionEnd={handleTransitionEnd}
		>
			{/* Backdrop */}
			<div
				className={`absolute inset-0 bg-[#2D343F4D] transition-opacity ${
					visible
						? "opacity-100 duration-300 ease-out"
						: "opacity-0 duration-200 ease-in"
				}`}
				onClick={onClose}
				aria-hidden="true"
			/>

			{/* Modal Content */}
			<div
				className={`relative w-full max-w-[400px] transition-all ${
					visible
						? "opacity-100 scale-100 duration-300 ease-out"
						: "opacity-0 scale-95 duration-200 ease-in"
				}`}
			>
				<div
					className="relative flex w-full flex-col items-center gap-[24px] overflow-clip rounded-[12px] bg-white px-[24px] pb-[24px] pt-16 shadow-[0px_20px_24px_-4px_rgba(10,13,18,0.08),0px_8px_8px_-4px_rgba(10,13,18,0.03)]"
					data-name="CenteredModal"
				>
					{children}

					{/* Close Button */}
					<div className="absolute right-0 top-0">
						<IconButton
							type="Tertiary"
							icon="cross_outline"
							iconSize="Large"
							onClick={onClose}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
